import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  onSnapshot,
  query,
  where,
  serverTimestamp,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { firestore, storage, functions } from '../config/firebase';

const COLLECTION = 'privateLenderOffers';
const CONTRACT_PREFIX = 'private-lender-offers';

/**
 * Upload contract file for an offer. Returns fileRef to store.
 * @param {string} offerId
 * @param {File} file
 * @returns {Promise<{ storagePath: string, name: string, size?: number, uploadedAt: string }>}
 */
export async function uploadOfferContract(offerId, file) {
  if (!offerId || !file) throw new Error('offerId and file required');
  const fileId = `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
  const safeName = (file.name || 'contract.pdf').replace(/[^a-zA-Z0-9._-]/g, '_');
  const path = `${CONTRACT_PREFIX}/${offerId}/${fileId}_${safeName}`;
  const storageRefPath = ref(storage, path);
  await uploadBytes(storageRefPath, file);
  return {
    storagePath: path,
    name: file.name || safeName,
    size: file.size,
    uploadedAt: new Date().toISOString(),
  };
}

/**
 * Create a private lender financing offer.
 * @param {{ propertyId: string, ownerUid: string, lenderUid: string, lenderDisplayName?: string, comment?: string, principalAmount?: number, interestRate?: number, termMonths?: number, loanStartDate?: string, loanMaturityDate?: string, paymentFrequency?: string, interestPaymentDue?: string, interestCalculationMode?: string }} data
 * @param {File} [contractFile] - optional contract PDF to upload
 * @returns {Promise<string>} offer id
 */
export async function createPrivateLenderOffer(data, contractFile = null) {
  const {
    propertyId,
    ownerUid,
    lenderUid,
    lenderDisplayName,
    comment,
    principalAmount,
    interestRate,
    termMonths,
    loanStartDate,
    loanMaturityDate,
    paymentFrequency,
    interestPaymentDue,
    interestCalculationMode,
  } = data;
  if (!propertyId || !ownerUid || !lenderUid) {
    throw new Error('propertyId, ownerUid and lenderUid required');
  }
  let contractFileRef = null;
  if (contractFile) {
    // We need to add the doc first to get an id, then upload. So create with placeholder then update.
    const docRef = await addDoc(collection(firestore, COLLECTION), {
      propertyId,
      ownerUid,
      lenderUid,
      lenderDisplayName: lenderDisplayName || null,
      comment: comment || null,
      principalAmount: typeof principalAmount === 'number' && !Number.isNaN(principalAmount) ? principalAmount : null,
      interestRate: typeof interestRate === 'number' && !Number.isNaN(interestRate) ? interestRate : null,
      termMonths: typeof termMonths === 'number' && !Number.isNaN(termMonths) ? termMonths : null,
      loanStartDate: loanStartDate || null,
      loanMaturityDate: loanMaturityDate || null,
      paymentFrequency: paymentFrequency || null,
      interestPaymentDue: interestPaymentDue === 'beginning_of_month' ? 'beginning_of_month' : 'end_of_month',
      interestCalculationMode: interestCalculationMode === 'monthly' ? 'monthly' : 'daily',
      status: 'pending',
      contractFileRef: null,
      contractSummary: null,
      signedContractFileRef: null,
      respondedAt: null,
      acceptedAt: null,
      rejectedAt: null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    contractFileRef = await uploadOfferContract(docRef.id, contractFile);
    await updateDoc(docRef, {
      contractFileRef,
      updatedAt: serverTimestamp(),
    });
    return docRef.id;
  }
  const docRef = await addDoc(collection(firestore, COLLECTION), {
    propertyId,
    ownerUid,
    lenderUid,
    lenderDisplayName: lenderDisplayName || null,
    comment: comment || null,
    principalAmount: typeof principalAmount === 'number' && !Number.isNaN(principalAmount) ? principalAmount : null,
    interestRate: typeof interestRate === 'number' && !Number.isNaN(interestRate) ? interestRate : null,
    termMonths: typeof termMonths === 'number' && !Number.isNaN(termMonths) ? termMonths : null,
    loanStartDate: loanStartDate || null,
    loanMaturityDate: loanMaturityDate || null,
    paymentFrequency: paymentFrequency || null,
    interestPaymentDue: interestPaymentDue === 'beginning_of_month' ? 'beginning_of_month' : 'end_of_month',
    interestCalculationMode: interestCalculationMode === 'monthly' ? 'monthly' : 'daily',
    status: 'pending',
    contractFileRef: contractFileRef || null,
    contractSummary: null,
    signedContractFileRef: null,
    respondedAt: null,
    acceptedAt: null,
    rejectedAt: null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return docRef.id;
}

/**
 * @param {string} offerId
 * @returns {Promise<object | null>}
 */
export async function getPrivateLenderOffer(offerId) {
  if (!offerId) return null;
  const docRef = doc(firestore, COLLECTION, offerId);
  const snap = await getDoc(docRef);
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

/**
 * Subscribe to all offers for a property (for owner Building Dashboard).
 * @param {string} propertyId
 * @param {string} ownerUid
 * @param {(list: Array<object>) => void} callback
 * @returns {() => void} unsubscribe
 */
export function subscribePrivateLenderOffersForProperty(propertyId, ownerUid, callback) {
  if (!propertyId || !ownerUid) {
    callback([]);
    return () => {};
  }
  const q = query(
    collection(firestore, COLLECTION),
    where('propertyId', '==', propertyId),
    where('ownerUid', '==', ownerUid)
  );
  return onSnapshot(
    q,
    (snapshot) => callback(snapshot.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (err) => {
      console.warn('subscribePrivateLenderOffersForProperty error', err);
      callback([]);
    }
  );
}

/**
 * Subscribe to offers for one property and one lender (for lender viewing client property).
 * @param {string} propertyId
 * @param {string} lenderUid
 * @param {(list: Array<object>) => void} callback
 * @returns {() => void} unsubscribe
 */
export function subscribePrivateLenderOffersByPropertyAndLender(propertyId, lenderUid, callback) {
  if (!propertyId || !lenderUid) {
    callback([]);
    return () => {};
  }
  const q = query(
    collection(firestore, COLLECTION),
    where('propertyId', '==', propertyId),
    where('lenderUid', '==', lenderUid)
  );
  return onSnapshot(
    q,
    (snapshot) => callback(snapshot.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (err) => {
      console.warn('subscribePrivateLenderOffersByPropertyAndLender error', err);
      callback([]);
    }
  );
}

/**
 * Subscribe to all offers created by a lender (for Gestion des prêts page).
 * @param {string} lenderUid
 * @param {(list: Array<object>) => void} callback
 * @returns {() => void} unsubscribe
 */
export function subscribePrivateLenderOffersByLender(lenderUid, callback) {
  if (!lenderUid) {
    callback([]);
    return () => {};
  }
  const q = query(
    collection(firestore, COLLECTION),
    where('lenderUid', '==', lenderUid)
  );
  return onSnapshot(
    q,
    (snapshot) => callback(snapshot.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (err) => {
      console.warn('subscribePrivateLenderOffersByLender error', err);
      callback([]);
    }
  );
}

/**
 * Update offer (status, signedContractFileRef, contractSummary, etc.).
 * @param {string} offerId
 * @param {{ status?: string, signedContractFileRef?: object, contractSummary?: string | object, respondedAt?: object, acceptedAt?: object, rejectedAt?: object }} data
 */
export async function updatePrivateLenderOffer(offerId, data) {
  if (!offerId) return;
  const docRef = doc(firestore, COLLECTION, offerId);
  const update = { updatedAt: serverTimestamp() };
  if (data.status !== undefined) update.status = data.status;
  if (data.signedContractFileRef !== undefined) update.signedContractFileRef = data.signedContractFileRef;
  if (data.contractSummary !== undefined) update.contractSummary = data.contractSummary;
  if (data.respondedAt !== undefined) update.respondedAt = data.respondedAt;
  if (data.acceptedAt !== undefined) update.acceptedAt = data.acceptedAt;
  if (data.rejectedAt !== undefined) update.rejectedAt = data.rejectedAt;
  await updateDoc(docRef, update);
}

/**
 * Investor rejects the offer.
 * @param {string} offerId
 */
export async function rejectPrivateLenderOffer(offerId) {
  await updatePrivateLenderOffer(offerId, {
    status: 'rejected',
    respondedAt: serverTimestamp(),
    rejectedAt: serverTimestamp(),
  });
}

/**
 * Lender cancels an offer (only when status is pending / not yet signed).
 * @param {string} offerId
 */
export async function cancelPrivateLenderOfferByLender(offerId) {
  await updatePrivateLenderOffer(offerId, {
    status: 'cancelled',
    respondedAt: serverTimestamp(),
  });
}

/**
 * Upload signed contract and accept the offer.
 * @param {string} offerId
 * @param {File} file
 */
export async function acceptPrivateLenderOfferWithSignedContract(offerId, file) {
  if (!offerId || !file) throw new Error('offerId and file required');
  const fileId = `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
  const safeName = (file.name || 'signed-contract.pdf').replace(/[^a-zA-Z0-9._-]/g, '_');
  const path = `${CONTRACT_PREFIX}/${offerId}/signed_${fileId}_${safeName}`;
  const storageRefPath = ref(storage, path);
  await uploadBytes(storageRefPath, file);
  const signedContractFileRef = {
    storagePath: path,
    name: file.name || safeName,
    size: file.size,
    uploadedAt: new Date().toISOString(),
  };
  await updatePrivateLenderOffer(offerId, {
    status: 'accepted',
    signedContractFileRef,
    respondedAt: serverTimestamp(),
    acceptedAt: serverTimestamp(),
  });
}

/**
 * Get download URL for a stored file (contract or signed contract).
 * @param {string} storagePath
 * @returns {Promise<string>}
 */
export async function getOfferFileDownloadUrl(storagePath) {
  if (!storagePath) throw new Error('storagePath required');
  const r = ref(storage, storagePath);
  return getDownloadURL(r);
}

const summarizeOfferContractCallable = httpsCallable(functions, 'summarizePrivateLenderOfferContract');
const extractContractTermsCallable = httpsCallable(functions, 'extractPrivateLenderContractTerms');

/**
 * Read file as base64 string (for callable payload).
 * @param {File} file
 * @returns {Promise<string>}
 */
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      const base64 = typeof dataUrl === 'string' && dataUrl.startsWith('data:')
        ? dataUrl.replace(/^data:[^;]+;base64,/, '')
        : '';
      resolve(base64);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/**
 * Extract loan terms from contract PDF via AI; optionally verify against current values.
 * @param {File} file - PDF file
 * @param {object} [currentValues] - { principalAmount?, interestRate?, termMonths?, loanStartDate?, loanMaturityDate?, paymentFrequency? }
 * @returns {Promise<{ extracted: object, verification?: object }>}
 */
export async function extractContractTermsFromPdf(file, currentValues = null) {
  if (!file) throw new Error('file required');
  const pdfBase64 = await fileToBase64(file);
  const { data } = await extractContractTermsCallable({
    pdfBase64,
    ...(currentValues && typeof currentValues === 'object' ? { currentValues } : {}),
  });
  return {
    extracted: data?.extracted ?? {},
    verification: data?.verification ?? null,
  };
}

/**
 * Request AI summary of an offer's contract (Gemini via Vertex). Only property owner may call.
 * Generates and stores FR + EN; subsequent calls return the cached summaries.
 * @param {string} offerId
 * @returns {Promise<{ summaryFr: string, summaryEn: string }>}
 */
export async function summarizeOfferContract(offerId) {
  if (!offerId) throw new Error('offerId required');
  const { data } = await summarizeOfferContractCallable({ offerId });
  return {
    summaryFr: data?.summaryFr ?? '',
    summaryEn: data?.summaryEn ?? '',
  };
}

/**
 * Delete all offers for a property (owner deleting property).
 * @param {string} propertyId
 * @param {string} ownerUid
 */
export async function deletePrivateLenderOffersByProperty(propertyId, ownerUid) {
  if (!propertyId || !ownerUid) return;
  const q = query(
    collection(firestore, COLLECTION),
    where('propertyId', '==', propertyId),
    where('ownerUid', '==', ownerUid)
  );
  const snapshot = await getDocs(q);
  await Promise.all(snapshot.docs.map((d) => deleteDoc(doc(firestore, COLLECTION, d.id))));
}
